<template>
  <div class="calm">
    <!-- ═══════════════ HEADER ═══════════════ -->
    <header class="calm__header">
      <div class="calm__header__row">
        <div>
          <div class="c-eyebrow">Invoice · v2</div>
          <h1 class="calm__header__title">{{ $t("menu.invoice") }}</h1>
          <div class="calm__header__sub">
            <span><strong class="c-mono">{{ total }}</strong> รายการ</span>
            <span class="calm__header__sep">·</span>
            <span class="c-mono">{{ todayStamp }}</span>
          </div>
        </div>
        <div class="calm__header__actions">
          <button class="c-btn c-btn--primary" @click="handleCreateBilling()">
            <i class="fas fa-plus"></i> {{ $t("new") }}
          </button>
        </div>
      </div>
    </header>

    <div class="calm__body">
      <!-- ═══════════════ FILTER CARD ═══════════════ -->
      <div class="calm__card">
        <div class="calm__card__head">
          <div>
            <div class="calm__card__title">ตัวกรอง</div>
            <div class="calm__card__hint">เลือกเงื่อนไขเพื่อค้นหารายการวางบิล</div>
          </div>
        </div>

        <div class="calm__filter-grid invoice-filter-grid">
          <div class="fld">
            <label>{{ $t("lmg.due_round") }}</label>
            <el-select v-model="searchParams.dueRound" :placeholder="$t('lmg.due_round')">
              <el-option v-for="o in dueRoundOptions" :key="o.ID" :label="o.Name" :value="o.ID"></el-option>
            </el-select>
          </div>
          <div class="fld">
            <label>{{ $t("lmg.month") }}</label>
            <el-select v-model="searchParams.dueMonth" :placeholder="$t('lmg.month')">
              <el-option v-for="o in monthOptions" :key="o.ID" :label="o.Name" :value="o.ID"></el-option>
            </el-select>
          </div>
          <div class="fld">
            <label>{{ $t("lmg.year") }}</label>
            <el-select v-model="searchParams.dueYear" :placeholder="$t('lmg.year')">
              <el-option v-for="o in yearOptions" :key="o" :label="o === 0 ? 'ทั้งหมด' : o" :value="o"></el-option>
            </el-select>
          </div>
          <div class="fld fld--span2">
            <label>{{ $t("lmg.person") }}</label>
            <el-select v-model="searchParams.personId" :placeholder="$t('lmg.person')" filterable>
              <el-option
                v-for="o in PersonTable"
                :key="o.ID"
                :label="o.FName + ' ' + o.LName"
                :value="o.ID"
              ></el-option>
            </el-select>
          </div>
          <div class="fld">
            <label>{{ $t("status") }}</label>
            <el-select v-model="searchParams.status" :placeholder="$t('status')">
              <el-option v-for="o in InvoiceStatus" :key="o.ID" :label="o.Name" :value="o.ID"></el-option>
            </el-select>
          </div>
          <div class="fld fld--span2">
            <label>{{ $t("lmg.invoice_number") }}</label>
            <input
              type="text"
              class="ed c-mono"
              v-model="searchParams.invoiceNumber"
              :placeholder="$t('lmg.invoice_number')"
            />
          </div>
          <div class="fld fld--span2">
            <label>{{ $t("lmg.policy_number") }}</label>
            <input
              type="text"
              class="ed c-mono"
              v-model="searchParams.policyNumber"
              :placeholder="$t('lmg.policy_number')"
            />
          </div>
        </div>

        <div class="invoice-filter-actions">
          <button class="c-btn c-btn--primary" :disabled="isLoading" @click="listData(params)">
            <i class="fas fa-spinner fa-spin" v-if="isLoading"></i>
            <i class="fas fa-search" v-else></i>
            {{ $t("search") }}
          </button>
        </div>
      </div>

      <!-- ═══════════════ TABLE CARD ═══════════════ -->
      <div class="calm__card calm__card--flush" style="margin-top:16px">
        <div class="invoice-table-head">
          <div>
            <div class="calm__card__title">รายการวางบิล</div>
            <div class="calm__card__hint" v-if="tableData.length">
              {{ from + 1 }}–{{ to }} จาก {{ total }} รายการ
            </div>
          </div>
          <div class="invoice-table-toolbar">
            <input
              v-model="searchQuery"
              type="text"
              class="ed"
              :placeholder="$t('search') + '…'"
              style="width:240px;max-width:100%"
            />
            <div class="calm__tfoot__per">
              <span>แสดงต่อหน้า</span>
              <el-select v-model="pagination.perPage" style="width:90px">
                <el-option
                  v-for="item in pagination.perPageOptions"
                  :key="item"
                  :label="item"
                  :value="item"
                ></el-option>
              </el-select>
            </div>
          </div>
        </div>

        <el-table
          :data="queriedData"
          row-key="id"
          @sort-change="sortChange"
          @selection-change="selectionChange"
          :empty-text="isLoading ? 'กำลังโหลด…' : 'ไม่พบรายการ'"
        >
          <el-table-column :label="$t('lmg.person')" min-width="200">
            <div slot-scope="{ row }">
              <div class="stack__primary">{{ row.PersonFName }} {{ row.PersonLName }}</div>
            </div>
          </el-table-column>

          <el-table-column :label="$t('lmg.invoice_number')" min-width="180">
            <div slot-scope="{ row }">
              <span class="c-mono">{{ row.InvoiceNumber }}</span>
            </div>
          </el-table-column>

          <el-table-column :label="$t('lmg.invoice_pay_amount')" min-width="150" align="right">
            <div slot-scope="{ row }">
              <span class="c-mono money">{{ FormatCurrency(row.PayAmount) }}</span>
            </div>
          </el-table-column>

          <el-table-column :label="$t('lmg.paid_status')" min-width="140">
            <div slot-scope="{ row }">
              <span class="calm__pill" :class="invoicePillClass(row.Status)">
                {{ getPaidStatus(row.Status) }}
              </span>
            </div>
          </el-table-column>

          <el-table-column :label="$t('created_at')" min-width="170">
            <div slot-scope="{ row }">
              <span class="c-mono">{{ row.CreatedAtShow }}</span>
            </div>
          </el-table-column>

          <el-table-column :label="$t('actions')" min-width="170" align="right">
            <div slot-scope="{ row }" class="row-actions">
              <button class="rowbtn" :title="$t('lmg.view_detail')" @click="handleViewDetail(row)">
                <i class="fas fa-eye"></i>
              </button>
              <button class="rowbtn" title="PDF" @click="handlePDFBilling(row)">
                <i class="fa fa-download"></i>
              </button>
              <!-- เพิ่มรายการเข้าใบวางบิล — ใบที่ยังไม่ชำระเท่านั้น (status 1) -->
              <button
                class="rowbtn"
                v-if="row.Status == 1"
                :title="$t('lmg.invoice_add_billing')"
                @click="handleAddBilling(row)"
              >
                <i class="fas fa-cart-plus"></i>
              </button>
              <button
                class="rowbtn"
                v-if="row.Status == 1"
                :title="$t('lmg.payment')"
                @click="handlePaidBilling(row)"
              >
                <i class="fas fa-dollar-sign"></i>
              </button>
            </div>
          </el-table-column>
        </el-table>

        <div class="calm__tfoot" style="padding:14px 22px" v-if="tableData.length">
          <div class="calm__tfoot__count">
            แสดง <strong>{{ from + 1 }}</strong>–<strong>{{ to }}</strong> จาก <strong>{{ total }}</strong> รายการ
            <span v-if="selectedRows.length">
              ·&nbsp;&nbsp;{{ selectedRows.length }} {{ $t("row_select") }}
            </span>
          </div>
          <base-pagination
            v-model="pagination.currentPage"
            :per-page="pagination.perPage"
            :total="total"
          ></base-pagination>
        </div>
      </div>
    </div>





	<!-- Modal Detail -->
    <modal
      :show.sync="showDetailModal"
      modal-classes="modal-secondary modal-lg"
    >
      <div class="invoice-detail">
        <!-- Header -->
        <div class="detail-header">
          <div>
            <h4>
              <i class="fas fa-file-invoice mr-2"></i>{{ $t("lmg.invoice") }}
              <span class="detail-id">{{ detail.InvoiceNumber || ('#' + detail.ID) }}</span>
            </h4>
          </div>
          <div class="d-flex align-items-center">
            <span :class="getPaidStatusClass(detail.Status)" class="mr-2">
              <i class="fas fa-check-circle" v-if="detail.Status == 2"></i>
              <i class="fas fa-clock"         v-else-if="detail.Status == 1"></i>
              <i class="fas fa-ban"           v-else-if="detail.Status == 3"></i>
              {{ getPaidStatus(detail.Status) }}
            </span>
            <!-- เพิ่มรายการเข้าใบวางบิล — ใบที่ยังไม่ชำระเท่านั้น (status 1) -->
            <base-button
              v-if="detail.Status == 1"
              size="sm"
              type="primary"
              class="mr-2"
              :title="$t('lmg.invoice_add_billing')"
              @click="handleAddBilling(detail)"
            >
              <i class="fas fa-cart-plus mr-1"></i>{{ $t("lmg.invoice_add_billing") }}
            </base-button>
            <base-button
              size="sm"
              type="warning"
              :title="$t('download')"
              @click="handlePDFBilling(detail)"
            >
              <i class="fas fa-download mr-1"></i>PDF
            </base-button>
          </div>
        </div>

        <!-- SECTION: Invoice info -->
        <div class="detail-section">
          <div class="detail-section-header"><i class="fas fa-info-circle"></i>{{ $t('lmg.invoice_number') }}</div>
          <div class="row">
            <div class="col-lg-4">
              <base-input :label="$t('lmg.invoice_number')" :value="detail.InvoiceNumber" readonly></base-input>
            </div>
            <div class="col-lg-4">
              <base-input :label="$t('lmg.person')" :value="(detail.PersonFName || '') + ' ' + (detail.PersonLName || '')" readonly></base-input>
            </div>
            <div class="col-lg-4">
              <base-input :label="$t('lmg.tel')" :value="detail.PersonTel || '-'" readonly></base-input>
            </div>
          </div>
          <div class="row">
            <div class="col-lg-4">
              <base-input :label="$t('created_at')" :value="detail.CreatedAtShow || '-'" readonly></base-input>
            </div>
            <div class="col-lg-4">
              <base-input :label="$t('lmg.updated_at')" :value="detail.UpdatedAtShow || '-'" readonly></base-input>
            </div>
            <div class="col-lg-4">
              <base-input :label="$t('lmg.paid_date')" :value="detail.PaidAtShow || '-'" readonly></base-input>
            </div>
          </div>
        </div>

        <!-- SECTION: Financial breakdown -->
        <div class="detail-section detail-section--financial">
          <div class="detail-section-header"><i class="fas fa-coins"></i>{{ $t('lmg.invoice_amount') }}</div>
          <div class="row">
            <div class="col-lg-3">
              <base-input :label="$t('lmg.invoice_amount')" :value="FormatCurrency(detail.Amount)" readonly></base-input>
            </div>
            <div class="col-lg-3">
              <base-input :label="$t('lmg.invoice_amount_all')" :value="FormatCurrency(detail.AmountAll)" readonly></base-input>
            </div>
            <div class="col-lg-3">
              <base-input :label="$t('lmg.tax_amount')" :value="FormatCurrency(detail.TaxAmount)" readonly></base-input>
            </div>
            <div class="col-lg-3">
              <base-input :label="$t('lmg.discount')" :value="FormatCurrency(detail.CommissionAmount)" readonly></base-input>
            </div>
          </div>
          <div class="row">
            <div class="col-lg-3">
              <base-input :label="$t('lmg.net_amount')" :value="FormatCurrency(detail.NetAmount)" readonly></base-input>
            </div>
            <div class="col-lg-3">
              <base-input :label="$t('lmg.invoice_pay_amount')" :value="FormatCurrency(detail.PayAmount)" readonly></base-input>
            </div>
            <div class="col-lg-3">
              <base-input :label="$t('lmg.commission_percent')" :value="(detail.CommissionPercent || 0) + ' %'" readonly></base-input>
            </div>
            <div class="col-lg-3">
              <base-input :label="$t('lmg.tax_percent')" :value="(detail.TaxPercent || 0) + ' %'" readonly></base-input>
            </div>
          </div>
        </div>

        <!-- SECTION: Payment info (only when paid) -->
        <div class="detail-section" v-if="detail.Status == 2">
          <div class="detail-section-header"><i class="fas fa-money-check-alt"></i>{{ $t('lmg.payment') }}</div>
          <div class="row">
            <div class="col-lg-4">
              <base-input :label="$t('lmg.paid_via')" :value="getPaymentViaName(detail.PaymentVia)" readonly></base-input>
            </div>
            <div class="col-lg-4">
              <base-input :label="$t('lmg.bank')" :value="getBankName(detail.PaymentBankID)" readonly></base-input>
            </div>
            <div class="col-lg-4">
              <base-input :label="$t('lmg.paid_date')" :value="detail.PaidAtShow || '-'" readonly></base-input>
            </div>
          </div>
          <div class="row" v-if="detail.TranferImage">
            <div class="col-lg-12">
              <label class="form-control-label">{{ $t('lmg.tranfer_image') }}</label>
              <div>
                <base-button size="sm" type="info" @click="handleViewInvoiceImage()">
                  <i class="fas fa-image mr-1"></i>{{ detail.TranferImage }}
                </base-button>
              </div>
            </div>
          </div>
        </div>

        <!-- SECTION: Covered billings — list of policy numbers in this invoice. -->
        <!-- Each row links into the transaction list pre-filtered by policy_number. -->
        <div class="detail-section" v-if="invoiceBillings && invoiceBillings.length">
          <div class="detail-section-header">
            <i class="fas fa-list"></i>{{ $t('lmg.sale_list') }} ({{ invoiceBillings.length }})
          </div>
          <el-table
            :data="invoiceBillings"
            row-key="billing_id"
            header-row-class-name="thead-light"
            v-loading="isLoadingInvoiceBillings"
            size="mini"
          >
            <el-table-column :label="$t('lmg.policy_number')" min-width="180">
              <template slot-scope="{ row }">
                <a href="#" class="text-primary mr-2" @click.prevent="handlePolicyClick(row)">
                  {{ row.policy_number || ('#' + row.transaction_id) }}
                </a>
                <base-button
                  v-if="row.policy_number"
                  size="sm"
                  type="secondary"
                  :title="$t('copy')"
                  @click="handleCopyPolicyNumber(row.policy_number)"
                >
                  <i class="fas fa-copy"></i>
                </base-button>
              </template>
            </el-table-column>
            <el-table-column :label="$t('lmg.customer')" min-width="180">
              <template slot-scope="{ row }">
                {{ fullName(row.customer_fname, row.customer_lname) }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('lmg.owner_person')" min-width="180">
              <template slot-scope="{ row }">
                {{ fullName(row.owner_fname, row.owner_lname) }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('lmg.trans_type')" min-width="110">
              <!-- renew absent ⇒ "-", never a confident "งานใหม่" that may be wrong -->
              <template slot-scope="{ row }">
                <span
                  v-if="row.renew != null"
                  class="badge"
                  :class="row.renew == 2 ? 'badge-warning' : 'badge-success'"
                >
                  {{ row.renew == 2 ? "งานต่ออายุ" : "งานใหม่" }}
                </span>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('lmg.invoice_amount')" min-width="120" align="right">
              <template slot-scope="{ row }">
                {{ row.amount != null ? FormatCurrency(row.amount) : '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('lmg.due_date')" min-width="110">
              <template slot-scope="{ row }">{{ row.DueDateShow || '-' }}</template>
            </el-table-column>
          </el-table>
        </div>

        <!-- SECTION: Note -->
        <div class="detail-section" v-if="detail.Note">
          <div class="detail-section-header"><i class="fas fa-sticky-note"></i>{{ $t('lmg.note') }}</div>
          <div class="row">
            <div class="col-lg-12">
              <base-input :value="detail.Note" readonly></base-input>
            </div>
          </div>
        </div>
      </div>

      <template slot="footer">
        <button
          type="button"
          class="btn btn-md btn-link ml-auto"
          @click="showDetailModal = false"
        >
          {{ $t("close") }}
        </button>
      </template>
    </modal>

	<!-- Modal: create invoice (select billings) -->
    <modal
      :show.sync="showBillingModal"
      modal-classes="modal-secondary modal-xl calm-modal"
    >
      <div class="calm calm-modal-body">
        <!-- Modal header -->
        <div class="bm-head">
          <div>
            <div class="c-eyebrow">{{ addMode ? "Add to invoice" : "Create invoice" }}</div>
            <h2 class="bm-title">
              <i class="fas fa-file-invoice-dollar"></i>
              {{ addMode ? $t("lmg.invoice_add_billing") : $t("lmg.billing") }}
            </h2>
            <div class="c-soft" style="font-size:13px" v-if="addMode">
              {{ $t("lmg.invoice_add_billing_hint") }}
              <strong class="c-mono">{{ addTarget.InvoiceNumber }}</strong>
              · {{ addTarget.PersonFName }} {{ addTarget.PersonLName }}
            </div>
            <div class="c-soft" style="font-size:13px" v-else>เลือกรายการที่ต้องการรวมเป็นใบวางบิล</div>
          </div>
          <div class="bm-meta" v-if="selectedBillings.length">
            <div class="c-eyebrow">เลือกแล้ว</div>
            <div class="bm-meta__value">
              <strong class="c-mono">{{ selectedBillings.length }}</strong>
              <span class="c-soft" style="font-size:13px;margin-left:6px">รายการ</span>
            </div>
            <!-- ยอดรวมชำระ ก่อน → หลัง เพิ่มรายการ -->
            <div class="c-soft bm-meta__delta" v-if="addMode">
              <span class="c-mono">{{ FormatCurrency(addTarget.PayAmount) }}</span>
              <i class="fas fa-arrow-right"></i>
              <strong class="c-mono">{{ FormatCurrency(addNewPayAmount) }}</strong>
            </div>
          </div>
        </div>

        <!-- Filter card -->
        <div class="bm-section">
          <div class="bm-section__head">
            <div class="c-eyebrow">Filter</div>
            <div class="c-soft" style="font-size:12.5px">เลือกเงื่อนไขเพื่อค้นหารายการ</div>
          </div>

          <div class="calm__filter-grid bm-filter-grid">
            <div class="fld">
              <label>{{ $t("lmg.person") }}</label>
              <!-- pinned to the invoice's own agent while extending an invoice -->
              <el-select
                v-model="paramsBillingCreate.PersonID"
                :placeholder="$t('lmg.person')"
                filterable
                :disabled="addMode"
              >
                <el-option
                  v-for="o in PersonTable"
                  :key="o.ID"
                  :label="o.FName + ' ' + o.LName"
                  :value="o.ID"
                ></el-option>
              </el-select>
            </div>
            <div class="fld">
              <label>{{ $t("lmg.policy_number") }}</label>
              <input
                type="text"
                class="ed c-mono"
                v-model="paramsBillingCreate.PolicyNumber"
                :placeholder="$t('lmg.policy_number')"
              />
            </div>
            <div class="fld">
              <label>{{ $t("lmg.customer") }}</label>
              <input
                type="text"
                class="ed"
                v-model="paramsBillingCreate.CustomerName"
                :placeholder="$t('lmg.customer')"
              />
            </div>
            <div class="fld">
              <label>{{ $t("lmg.owner_person") }}</label>
              <input
                type="text"
                class="ed"
                v-model="paramsBillingCreate.OwnerPersonName"
                :placeholder="$t('lmg.owner_person')"
              />
            </div>
            <div class="fld">
              <label>{{ $t("lmg.trans_type") }}</label>
              <el-select v-model="paramsBillingCreate.Renew" :placeholder="$t('lmg.trans_type')">
                <el-option v-for="o in renewOptions" :key="o.ID" :label="o.Name" :value="o.ID"></el-option>
              </el-select>
            </div>
            <div class="fld">
              <label>{{ $t("lmg.due_round") }}</label>
              <el-select v-model="paramsBillingCreate.DueRound" :placeholder="$t('lmg.due_round')">
                <el-option v-for="o in dueRoundOptions" :key="o.ID" :label="o.Name" :value="o.ID"></el-option>
              </el-select>
            </div>
            <div class="fld">
              <label>{{ $t("lmg.month") }}</label>
              <el-select v-model="paramsBillingCreate.DueMonth" :placeholder="$t('lmg.month')">
                <el-option v-for="o in monthOptions" :key="o.ID" :label="o.Name" :value="o.ID"></el-option>
              </el-select>
            </div>
            <div class="fld">
              <label>{{ $t("lmg.year") }}</label>
              <el-select v-model="paramsBillingCreate.DueYear" :placeholder="$t('lmg.year')">
                <el-option v-for="o in yearOptions" :key="o" :label="o === 0 ? 'ทั้งหมด' : o" :value="o"></el-option>
              </el-select>
            </div>
          </div>

          <div class="bm-search-row">
            <button
              class="c-btn c-btn--primary"
              :disabled="isLoadingBilling"
              @click="searchBilling"
            >
              <i class="fas fa-spinner fa-spin" v-if="isLoadingBilling"></i>
              <i class="fas fa-search" v-else></i>
              {{ $t("search") }}
            </button>
          </div>
        </div>

        <!-- Result table -->
        <div class="bm-section bm-section--table">
          <div class="bm-section__head">
            <div class="c-eyebrow">Bills available</div>
            <div class="c-soft" style="font-size:12.5px">
              <span v-if="pickerBillings.length">
                พบ <strong class="c-mono">{{ pickerBillings.length }}</strong> รายการ ·
                <!-- scope wording follows the actual rule: one agent, or the
                     whole customer-commission tier group -->
                <span v-if="addMode">{{ addTargetIsCustomerCom
                  ? $t("lmg.invoice_add_billing_scope_group")
                  : $t("lmg.invoice_add_billing_scope_agent") }}</span>
                <span v-else>ติ๊กรายการที่ต้องการรวมเป็นใบวางบิลเดียว</span>
              </span>
              <span v-else-if="addMode && !isLoadingBilling">{{ $t("lmg.invoice_add_billing_no_rows") }}</span>
              <span v-else>กดค้นหาเพื่อแสดงรายการ</span>
              <span
                v-if="rawBillings.length >= 5000"
                style="display:block; margin-top:4px; color:var(--c-danger, #c0573e); font-weight:600"
              >
                ⚠️ ผลลัพธ์อาจไม่ครบ (เกิน 5000 รายการ) — กรุณากรองเพิ่ม (ชื่อลูกค้า / เดือน / ปี / รอบ)
              </span>
            </div>
          </div>

          <div class="bm-table-wrap">
            <el-table
              ref="billingPicker"
              :data="pickerBillings"
              row-key="id"
              @selection-change="onSelectionChange"
              :empty-text="isLoadingBilling ? 'กำลังโหลด…' : 'ไม่พบรายการ'"
            >
              <el-table-column type="selection" align="center" width="48"></el-table-column>
              <el-table-column :label="$t('lmg.customer')" min-width="160">
                <template slot-scope="{ row }">
                  <div class="stack__primary">{{ fullName(row.customer_fname, row.customer_lname) }}</div>
                </template>
              </el-table-column>
              <el-table-column :label="$t('lmg.owner_person')" min-width="160">
                <template slot-scope="{ row }">
                  <div class="stack__primary">{{ fullName(row.owner_fname, row.owner_lname) }}</div>
                </template>
              </el-table-column>
              <el-table-column :label="$t('lmg.policy_number')" min-width="140">
                <template slot-scope="{ row }">
                  <span class="c-mono">{{ row.policy_number || '—' }}</span>
                </template>
              </el-table-column>
              <el-table-column :label="$t('lmg.trans_type')" min-width="110">
                <!-- renew absent ⇒ "-", never a confident "งานใหม่" that may be wrong -->
                <template slot-scope="{ row }">
                  <span
                    v-if="row.renew != null"
                    class="calm__pill"
                    :class="row.renew == 2 ? 'calm__pill--warning' : 'calm__pill--success'"
                  >
                    {{ row.renew == 2 ? "ต่ออายุ" : "งานใหม่" }}
                  </span>
                  <span v-else class="c-soft">-</span>
                </template>
              </el-table-column>
              <el-table-column :label="$t('lmg.invoice_amount')" min-width="120" align="right">
                <template slot-scope="{ row }">
                  <span class="c-mono">{{ FormatCurrency(row.amount) }}</span>
                </template>
              </el-table-column>
              <el-table-column :label="$t('lmg.invoice_amount_all')" min-width="120" align="right">
                <template slot-scope="{ row }">
                  <span class="c-mono c-soft">{{ FormatCurrency(row.amount_all) }}</span>
                </template>
              </el-table-column>
              <el-table-column :label="$t('lmg.due_date')" min-width="110">
                <template slot-scope="{ row }">
                  <span class="c-mono">{{ row.DueDateShow }}</span>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>

        <!-- Note row — create only; extending an invoice keeps its original note -->
        <div class="bm-note" v-if="!addMode">
          <div class="fld">
            <label>{{ $t("lmg.note") }}</label>
            <input
              type="text"
              class="ed"
              v-model="paramsBilling.Note"
              :placeholder="$t('lmg.note')"
            />
          </div>
        </div>
      </div>

      <template slot="footer">
        <div class="calm calm-modal-foot">
          <button
            type="button"
            class="c-btn c-btn--ghost"
            @click="showBillingModal = false"
          >
            {{ $t("close") }}
          </button>
          <button
            type="submit"
            class="c-btn c-btn--primary"
            :disabled="!selectedBillings.length || isSubmittingAdd"
            @click="addMode ? handleAddBillingSubmit() : handleUpdateBilling()"
          >
            <i class="fas fa-spinner fa-spin" v-if="isSubmittingAdd"></i>
            <i class="fas fa-check" v-else></i>
            {{ $t("save") }}
            <span v-if="selectedBillings.length" style="opacity:0.8">· {{ selectedBillings.length }}</span>
          </button>
        </div>
      </template>
    </modal>



	<!-- Modal Paid -->
    <modal
      :show.sync="showPaidModal"
      modal-classes="modal-secondary modal-lg"
    >
	<validation-observer  ref="formValidator">
	<!-- Receiving account (from /api/bank/for-payment) — drives PaymentVia -->
	<div class="row">
		<div class="col-lg-6">
        <base-input
          :label="$t('lmg.account_select')"
          :name="$t('lmg.account_select')"
          input-classes="form-control-alternative new-event--title"
		  rules="required"
        >
          <el-select v-model="paidAccountID" :placeholder="$t('lmg.account_select')">
            <el-option
              v-for="option in forPaymentAccounts"
              :key="option.ID"
              :label="accountLabel(option)"
              :value="option.ID"
            >
            </el-option>
          </el-select>
        </base-input>
		</div>
	</div>

	<!-- Per-billing commission_deducted -->
	<div class="row" v-if="invoiceBillings && invoiceBillings.length">
		<div class="col-lg-12">
			<label class="form-control-label">{{ $t('lmg.sale_list') }} ({{ invoiceBillings.length }})</label>
			<el-table
				:data="invoiceBillings"
				row-key="billing_id"
				header-row-class-name="thead-light"
				v-loading="isLoadingInvoiceBillings"
				size="mini"
			>
				<el-table-column :label="$t('lmg.policy_number')" min-width="160">
					<template slot-scope="{ row }">
						{{ row.policy_number || ('#' + row.transaction_id) }}
					</template>
				</el-table-column>
				<el-table-column label="เบี้ยรวม" min-width="110" align="right">
					<template slot-scope="{ row }">
						<span class="c-mono">{{ FormatCurrency(row.amount_all) }}</span>
					</template>
				</el-table-column>
				<el-table-column label="ยอดรวมชำระ" min-width="110" align="right">
					<template slot-scope="{ row }">
						<span class="c-mono c-soft">{{ FormatCurrency(row.pay_amount) }}</span>
					</template>
				</el-table-column>
				<el-table-column :label="$t('lmg.commission_amount')" min-width="110" align="right">
					<template slot-scope="{ row }">
						{{ row.commission_amount != null ? FormatCurrency(row.commission_amount) : '-' }}
					</template>
				</el-table-column>
				<el-table-column label="วิธีจ่ายของตัวแทน" min-width="150" align="center">
					<template slot-scope="{ row }">
						<!-- จ่ายเต็ม = โอนเบี้ยรวม (สาขาติดค้างคอมฯ) → deducted=0;
						     หักคอมฯ = หักคอมฯ แล้วโอนยอดรวมชำระ → deducted=1 -->
						<div class="qp-basis">
							<button type="button" class="qp-basis__btn" :class="{ 'qp-basis__btn--on': !paidCommission[row.billing_id] }" @click="$set(paidCommission, row.billing_id, false)">จ่ายเต็ม</button>
							<button type="button" class="qp-basis__btn" :class="{ 'qp-basis__btn--on': paidCommission[row.billing_id] }" @click="$set(paidCommission, row.billing_id, true)">หักคอมฯ</button>
						</div>
					</template>
				</el-table-column>
			</el-table>
		</div>
      </div>



	  <div class="row">
			<div class="col-lg-6">
              <base-input
                :label="$t('lmg.paid_file')"
                :name="$t('lmg.paid_file')"
                input-classes="form-control-alternative new-event--title"
              >
                <file-input multiple :initial-label="$t('lmg.add_files')" @change="filesChange"></file-input>
                <ul v-if="tfimgFiles.length" class="tf-files">
                  <li v-for="(f, i) in tfimgFiles" :key="i">
                    <span class="tf-files__name">{{ f.name }}</span>
                    <button type="button" class="tf-files__rm" :title="$t('lmg.remove_file')" @click="removeTfimgFile(i)">×</button>
                  </li>
                </ul>
              </base-input>
            </div>
			</div>

		<div class="row">
		<div class="col-lg-6">
			<base-input
                :label="$t('lmg.paid_time')"
                :name="$t('lmg.paid_time')"
				rules="required"
              >
                <flat-picker
                  slot-scope="{ focus, blur }"
                  @on-open="focus"
                  @on-close="blur"
                  class="form-control datepicker"
                  v-model="paramsPaid.PaidDate"
				  :config="formatDate"
                >
                </flat-picker>
              </base-input>
		</div>
	  </div>
	</validation-observer>




	  <template slot="footer">
		<button
          type="submit"
          class="btn btn-md btn-primary"
          @click="handleUpdatePaid()"
        >
          {{ $t("save") }}
        </button>

        <button
          type="button"
          class="btn btn-md btn-link ml-auto"
          @click="showPaidModal = false"
        >
          {{ $t("close") }}
        </button>
      </template>
    </modal>


  </div>
</template>
<script>
import { Table, TableColumn, Select, Option } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination } from "@/components";
import clientPaginationMixin from "../PaginatedTables/BillingPaginationMixin";
import swal from "sweetalert2";
import { mapGetters, mapActions } from "vuex";
import { LmgService } from "@/resource";
import FileInput from "@/components/Inputs/FileInput";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

// ── กลุ่มคอมมิชชันลูกค้าตรง (customer-commission tiers) ────────────────────
// These six person ids are NOT six different agents. They are commission-
// discount tiers for direct customers (branch 2), and one invoice may
// legitimately mix them — ~894 unpaid invoices already do:
//   195 = Com 0% (ไม่ลด)   193 = Com 05%           160 = Com 10%
//   194 = Com 15%          191 = Com 15% (เพื่อน)   190 = Com 18% (ญาติ)
// `/billing/choose` widens its PersonID filter across the whole group, and the
// add-billings guard treats any two of them as the same settlement party.
//
// Mirrors PERSON_CUSTOMER_COMMISSION_IDS / PersonIsCustomerCommission in the
// backend's controllers/Type.go. Defined once here — do not scatter the literal.
const CUSTOMER_COM_PERSON_IDS = [195, 193, 160, 191, 194, 190];

function isCustomerComTier(personID) {
  return CUSTOMER_COM_PERSON_IDS.includes(Number(personID));
}

export default {
  mixins: [clientPaginationMixin],
  components: {
    flatPicker,
    BasePagination,
    RouteBreadCrumb,
    FileInput,
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn
  },
  computed: {
    ...mapGetters({
		tableData: "Invoice/getData",
		tableBilling: "Invoice/getBilling",
		invoiceBillings: "Invoice/getInvoiceBillings",
		InvoiceStatus: "Invoice/getInvoiceStatus",
		PersonTableOrg: "Person/getData",
		tableBank: "Bank/getData",
		forPaymentAccounts: "Bank/getForPayment",
		isLoading: "Invoice/isLoading",
		isLoadingBilling: "Invoice/isLoadingBilling",
		isLoadingInvoiceBillings: "Invoice/isLoadingInvoiceBillings",
    }),
	selectedPaidAccount: function() {
		return this.forPaymentAccounts.find((a) => a.ID === this.paidAccountID) || null;
	},
	paidPaymentVia: function() {
		const acc = this.selectedPaidAccount;
		if (!acc) return 1;
		return Number(acc.AccountType) === 3 ? 1 : 3;
	},
	PersonTable : function(){
		return [{ID:0,FName:"ทั้งหมด",LName:""},...this.PersonTableOrg];
	},
	// The Invoice store seeds `billings` as {} before the first search, so
	// normalise to an array before anything iterates it.
	rawBillings : function(){
		return Array.isArray(this.tableBilling) ? this.tableBilling : [];
	},
	// Rows the picker actually offers.
	//  - create mode: whatever /billing/choose returned (already scoped to
	//    billing_invoice_id = 0 + this branch).
	//  - add mode: additionally narrowed to rows that settle onto THIS invoice.
	//    We re-filter rather than trust the response: /billing/choose widens its
	//    PersonID filter across the whole tier group, and a real agent's billing
	//    that isn't this invoice's agent must never be tickable, or staff would
	//    submit a row the server will refuse.
	//
	// This is the ONE definition of "settles onto this invoice" — the submit
	// path reuses the set rather than repeating the rule, so the two can't drift.
	pickerBillings : function(){
		if (!this.addMode) return this.rawBillings;
		const invoicePersonID = Number(this.addTarget.PersonID);
		// No agent on the invoice → offer nothing rather than everything.
		if (!invoicePersonID) return [];
		return this.rawBillings.filter((b) => {
			if (Number(b.billing_invoice_id) !== 0) return false;
			// Same agent …
			if (Number(b.person_id) === invoicePersonID) return true;
			// … or both sides are direct-customer commission tiers, which are
			// discount rates rather than distinct agents and settle together.
			return isCustomerComTier(b.person_id) && isCustomerComTier(invoicePersonID);
		});
	},
	// True when the invoice belongs to the customer-commission tier group, in
	// which case the picker legitimately offers rows from the sibling tiers too.
	addTargetIsCustomerCom : function(){
		return CUSTOMER_COM_PERSON_IDS.includes(Number(this.addTarget.PersonID));
	},
	// Preview of what the settlement document becomes if the operator confirms.
	addSelectedPayAmount : function(){
		return this.selectedBillings.reduce((sum, b) => sum + Number(b.pay_amount || 0), 0);
	},
	addNewPayAmount : function(){
		return Number(this.addTarget.PayAmount || 0) + this.addSelectedPayAmount;
	},
	yearOptions : function(){
		const currentYear = new Date().getFullYear();
		const years = [];
		for (let y = currentYear - 2; y <= currentYear + 1; y++) {
			years.push(y);
		}
		return [0, ...years];
	},
	params : function(){
		return {
			DueRound : this.searchParams.dueRound,
			DueMonth : this.searchParams.dueMonth,
			DueYear : this.searchParams.dueYear,
			PersonID : this.searchParams.personId == "" ? 0 : this.searchParams.personId,
			Status : this.searchParams.status,
			InvoiceNumber : this.searchParams.invoiceNumber,
			PolicyNumber : this.searchParams.policyNumber,
		};
	},
	todayStamp: function() {
		const d = new Date();
		const yyyy = d.getFullYear();
		const mm = String(d.getMonth() + 1).padStart(2, "0");
		const dd = String(d.getDate()).padStart(2, "0");
		const hh = String(d.getHours()).padStart(2, "0");
		const mi = String(d.getMinutes()).padStart(2, "0");
		return `${yyyy}/${mm}/${dd} ${hh}:${mi}`;
	},
  },
  watch: {
    // The billing-picker el-table is display:none while the create-invoice
    // modal is closed, so Element UI computes its column widths against a
    // zero-width container and the selection checkbox overlaps the first
    // data column. Re-run doLayout() once the modal (and table) is visible.
    showBillingModal(val) {
      if (val) {
        this.$nextTick(() => {
          setTimeout(() => {
            if (this.$refs.billingPicker) this.$refs.billingPicker.doLayout();
          }, 320);
        });
      } else {
        // Closing (X / close button / after submit) always drops back to the
        // default create-invoice mode so the next open can't inherit a stale
        // invoice target.
        this.addMode = false;
        this.addTarget = {};
      }
    },
  },
  data() {
    return {
      propsToSearch: ["Name", "AcountNumber"],
      selectedRows: [],
      // Bills currently checked in the create-invoice modal table.
      // Kept separate from `selectedRows` (which is the page-level list table
      // selection) so opening the modal doesn't clobber the list state.
      selectedBillings: [],
      // Billing modal runs in two modes: create a new invoice (default) or
      // extend an existing unpaid one. `addTarget` is the invoice being extended.
      addMode: false,
      addTarget: {},
      // In-flight guard for the add-to-invoice submit — see handleAddBillingSubmit.
      isSubmittingAdd: false,
      showBillingModal: false,
      showPaidModal: false,
      showDetailModal: false,
      detail: {},
	  paidType: [
        { ID: 1, Name: "เงินสด" },
        { ID: 2, Name: "เช็ค" },
        { ID: 3, Name: "โอนเงิน" }
      ],

      searchParams: {
        dueRound: 0,
        dueMonth: new Date().getMonth() + 1,
        dueYear: new Date().getFullYear(),
        personId: "", //1=DESC
        status: 0, //1 รอชำระเงิน, 2 ชำระเงินแล้ว, 3 ยกเลิก
        invoiceNumber:"",
        policyNumber:"",
      },
	  paramsBilling:{
		Note:"",
		BillingList:[],
	  },
	  paidAccountID: "",       // selected receiving account (from /api/bank/for-payment)
	  paidCommission: {},      // { [billing_id]: bool } — agent deducted commission before transfer
	  tfimgFiles: [],          // up to 5 transfer-proof files, appended under "Tfimg"
	  paramsPaid:{
		ID : "",
		PaymentVia : "",
		PaymentBankID : "",
		PaidDate : "",
	  },
	  paramsBillingCreate:{
		PersonID:0,
		PolicyNumber:"",
		CustomerName:"",
		OwnerPersonName:"",
		Renew:0, // 0 all, 1 งานใหม่, 2 งานต่ออายุ
		DueRound: 0,
		DueMonth: 0,
		DueYear: 0,
	  },
	  renewOptions: [
		{ ID: 0, Name: "ทั้งหมด" },
		{ ID: 1, Name: "งานใหม่" },
		{ ID: 2, Name: "งานต่ออายุ" },
	  ],
	  dueRoundOptions: [
		{ ID: 0, Name: "ไม่เลือก" },
		{ ID: 1, Name: "รอบที่ 1 (วันที่ 15)" },
		{ ID: 2, Name: "รอบที่ 2 (สิ้นเดือน)" },
	  ],
	  monthOptions: [
		{ ID: 0, Name: "ทั้งหมด" },
		{ ID: 1, Name: "มกราคม" },
		{ ID: 2, Name: "กุมภาพันธ์" },
		{ ID: 3, Name: "มีนาคม" },
		{ ID: 4, Name: "เมษายน" },
		{ ID: 5, Name: "พฤษภาคม" },
		{ ID: 6, Name: "มิถุนายน" },
		{ ID: 7, Name: "กรกฎาคม" },
		{ ID: 8, Name: "สิงหาคม" },
		{ ID: 9, Name: "กันยายน" },
		{ ID: 10, Name: "ตุลาคม" },
		{ ID: 11, Name: "พฤศจิกายน" },
		{ ID: 12, Name: "ธันวาคม" },
	  ],
	  formatDate: {
        enableTime: true,
        dateFormat: 'Y-m-d H:i'
    },

    };
  },
  async mounted() {
	this.listPerson();
	this.listBank();
	this.forPaymentData();
	// Deep-link: ?invoiceNumber=… (e.g. from Quick Payment "already invoiced")
	// pre-fills the invoice-number filter so the page lands pre-searched.
	// searchParams.invoiceNumber feeds the InvoiceNumber field of `params`,
	// which is the BE invoice-number filter.
	const qInvoiceNumber = this.$route.query.invoiceNumber;
	if (qInvoiceNumber) {
		this.searchParams.invoiceNumber = qInvoiceNumber;
	}
    await this.listData(this.params);
  },
  methods: {
    ...mapActions({
      createData: "Invoice/createData",
      addBillings: "Invoice/addBillings",
      paidData: "Invoice/paidData",
      reportData: "Invoice/reportData",
      listData: "Invoice/listData",
      listBilling: "Invoice/listBilling",
      clearBilling: "Invoice/clearBilling",
      listInvoiceBillings: "Invoice/listInvoiceBillings",
      clearInvoiceBillings: "Invoice/clearInvoiceBillings",
      listPerson: "Person/listData",
	  listBank: "Bank/listData",
	  forPaymentData: "Bank/forPaymentData",
    }),
	accountTypeLabel(type) {
		if (Number(type) === 1) return this.$t("lmg.account_type_hq");
		if (Number(type) === 2) return this.$t("lmg.account_type_branch_transfer");
		if (Number(type) === 3) return this.$t("lmg.account_type_branch_cash");
		return "";
	},
	accountLabel(a) {
		const num = a.AccountNumber ? " " + a.AccountNumber : "";
		const type = this.accountTypeLabel(a.AccountType);
		return `${a.Name}${num}${type ? " (" + type + ")" : ""}`;
	},

	// Join a first/last name pair for display. Returns "-" when the server sent
	// neither half, so a missing field reads as "no data" instead of the lone
	// space that `{{ a }} {{ b }}` produces.
	fullName(fname, lname) {
		const name = [fname, lname].filter((p) => p != null && p !== "").join(" ").trim();
		return name || "-";
	},

	getPaidStatus(t) {
      if (t == null || t === "") return "";
      const s = this.InvoiceStatus.filter(f => f.ID == t);
      return s.length ? s[0].Name : "";
    },
    invoicePillClass(s) {
      // 1 = รอชำระ, 2 = ชำระแล้ว, 3 = ยกเลิก
      if (s == 1) return "calm__pill--blue";
      if (s == 2) return "calm__pill--success";
      if (s == 3) return "calm__pill--neutral";
      return "calm__pill--neutral";
    },

    selectionChange(selectedRows) {
      this.selectedRows = selectedRows;
    },
    // Multi-file picker: validate (≤5 files, ≤5MB each, pdf/image only) and
    // append accepted files to tfimgFiles (keeps any already chosen).
    filesChange(files) {
      const MAX = 5;
      const MAX_SIZE = 5 * 1024 * 1024;
      const incoming = Array.from(files || []);
      for (const f of incoming) {
        const okType = f.type === "application/pdf" || /^image\//.test(f.type);
        if (!okType) {
          swal.fire({ title: this.$t("lmg.file_type_invalid"), text: f.name, icon: "error" });
          continue;
        }
        if (f.size > MAX_SIZE) {
          swal.fire({ title: this.$t("lmg.file_too_large"), text: f.name, icon: "error" });
          continue;
        }
        if (this.tfimgFiles.length >= MAX) {
          swal.fire({ title: this.$t("lmg.max_files_5"), icon: "error" });
          break;
        }
        this.tfimgFiles.push(f);
      }
    },
    removeTfimgFile(i) {
      this.tfimgFiles.splice(i, 1);
    },
	handleCreateBilling(){
		this.addMode = false;
		this.addTarget = {};
		this.showBillingModal = true;
		this.clearBilling();
		this.selectedBillings = [];
		this.paramsBilling.Note = "";
		// Reset filters to their defaults on open
		this.paramsBillingCreate.PersonID = 0;
		this.paramsBillingCreate.PolicyNumber = "";
		this.paramsBillingCreate.CustomerName = "";
		this.paramsBillingCreate.OwnerPersonName = "";
		this.paramsBillingCreate.Renew = 0;
		this.paramsBillingCreate.DueRound = 0;
		this.paramsBillingCreate.DueMonth = 0;
		this.paramsBillingCreate.DueYear = 0;
	},
	// Search billings for the create-invoice picker, then recompute the
	// el-table layout once the new rows are committed. Without this the
	// selection column width can be stale and overlap the first data column.
	searchBilling(){
		return this.listBilling(this.paramsBillingCreate).then(() => {
			this.$nextTick(() => {
				if (this.$refs.billingPicker) this.$refs.billingPicker.doLayout();
			});
		});
	},
	handleUpdateBilling(){

		this.paramsBilling.BillingList = [];

		// Pull from the modal's own selection set, not the page-level list selection.
		this.selectedBillings.map((item) => {
			this.paramsBilling.BillingList.push(item.id);
		});

		// console.log(this.paramsBilling)
		// return

		this.createData(this.paramsBilling)
          .then(resp => {
            this.listData(this.params);
			this.showBillingModal = false;
            swal.fire({
              title: this.$t("create_success"),
              text: ``,
              icon: "success",
              confirmButtonClass: "btn btn-success btn-fill",
              buttonsStyling: false
            });
          })
          .catch(err => {
            swal.fire({
              title: this.$t(err.response.data.Msg),
              text: '',
              icon: "error",
              confirmButtonClass: "btn btn-danger btn-fill",
              buttonsStyling: false
            });
          });

	},

	// ── เพิ่มรายการเข้าใบวางบิล ────────────────────────────────────────────
	// "วางบิลไปแล้ว แต่มีรายการขายเพิ่ม และชำระในสลิปเดียวกัน" — instead of
	// cancelling the invoice (which burns its number) or issuing a second one,
	// staff append the new billings to the invoice that is still awaiting
	// payment. Unpaid invoices (status 1) only; the guard is repeated here even
	// though the buttons are already v-if'd, because this is a settlement doc.
	handleAddBilling(row){
		if (!row || row.Status != 1) return;

		this.addMode = true;
		this.addTarget = row;
		this.showDetailModal = false;
		this.showBillingModal = true;

		this.clearBilling();
		this.selectedBillings = [];
		this.paramsBilling.Note = "";

		// Reuse the create-invoice picker, but pin its agent filter to the
		// invoice's own person (the select is disabled in add mode) and reset
		// every other filter so nothing carries over from a previous open.
		this.paramsBillingCreate.PersonID = row.PersonID || 0;
		this.paramsBillingCreate.PolicyNumber = "";
		this.paramsBillingCreate.CustomerName = "";
		this.paramsBillingCreate.OwnerPersonName = "";
		this.paramsBillingCreate.Renew = 0;
		this.paramsBillingCreate.DueRound = 0;
		this.paramsBillingCreate.DueMonth = 0;
		this.paramsBillingCreate.DueYear = 0;

		// Land pre-searched — the agent is already known, so an empty table
		// would just be an extra click.
		this.searchBilling();
	},
	async handleAddBillingSubmit(){
		// isSubmittingAdd also guards a double-click that lands before the
		// confirm dialog paints — two requests would fire and the second would
		// show a spurious error after the server (correctly) refused it.
		if (!this.addMode || this.isSubmittingAdd || !this.selectedBillings.length) return;

		// Snapshot before anything closes the modal — the showBillingModal
		// watcher clears addTarget on close.
		const invoiceID = this.addTarget.ID;
		const invoiceNumber = this.addTarget.InvoiceNumber || ("#" + invoiceID);
		const oldPayAmount = this.FormatCurrency(this.addTarget.PayAmount);
		// Re-apply the offerable rule at submit time, not just at render time —
		// the ticked set is the thing that reaches the server. Reusing
		// pickerBillings keeps one definition of the rule and also drops a row
		// that a re-search has since removed from the offer set.
		const offerable = new Set(this.pickerBillings.map((b) => b.id));
		const rows = this.selectedBillings.filter((item) => offerable.has(item.id));
		const billingList = rows.map((item) => item.id);
		if (!billingList.length) return;
		const rowCount = rows.length;
		const newPayAmount = this.FormatCurrency(
			Number(this.addTarget.PayAmount || 0) +
				rows.reduce((sum, b) => sum + Number(b.pay_amount || 0), 0)
		);

		this.isSubmittingAdd = true;
		try {

			// Show what changes before touching a settlement document.
			const confirmed = await swal.fire({
				title: this.$t("lmg.invoice_add_billing_confirm"),
				html:
					`${this.$t("lmg.invoice_number")} <strong>${invoiceNumber}</strong><br/>` +
					`${this.$t("lmg.invoice_add_billing_rows")} <strong>${rowCount}</strong> ${this.$t("lmg.items")}<br/>` +
					`${this.$t("lmg.invoice_pay_amount")} ${oldPayAmount} &rarr; <strong>${newPayAmount}</strong>`,
				icon: "warning",
				showCancelButton: true,
				confirmButtonText: this.$t("save"),
				cancelButtonText: this.$t("close"),
				confirmButtonClass: "btn btn-primary btn-fill",
				cancelButtonClass: "btn btn-secondary btn-fill",
				buttonsStyling: false
			});
			if (!confirmed.value) return;

			try {
				await this.addBillings({ InvoiceID: invoiceID, BillingList: billingList });
			} catch (err) {
				// Msg is an i18n key; Cause (when present) carries the specific Thai
				// reason from the server — e.g. which policy is already invoiced.
				const data = (err.response && err.response.data) || {};
				const cause = data.Cause && !/^[A-Za-z]/.test(data.Cause) ? "\n" + data.Cause : "";
				swal.fire({
					title: this.$t("lmg.invoice_add_billing_fail"),
					text: (data.Msg ? this.$t(data.Msg) : "") + cause,
					icon: "error",
					confirmButtonClass: "btn btn-danger btn-fill",
					buttonsStyling: false
				});
				return;
			}

			this.showBillingModal = false;

			// Refresh the list so the row shows its new total, then re-open the
			// detail modal on the fresh row so the added policies are visible.
			await this.listData(this.params);
			const fresh = (this.tableData || []).find((r) => r.ID === invoiceID);
			if (fresh) {
				this.detail = fresh;
				this.clearInvoiceBillings();
				this.listInvoiceBillings(invoiceID);
			}

			swal.fire({
				title: this.$t("lmg.invoice_add_billing_success"),
				text: ``,
				icon: "success",
				confirmButtonClass: "btn btn-success btn-fill",
				buttonsStyling: false
			}).then(() => {
				if (fresh) this.showDetailModal = true;
			});
		} finally {
			this.isSubmittingAdd = false;
		}
	},

	async handlePaidBilling(row){
		this.showPaidModal = true;
		this.paramsPaid.ID = row.ID;

		this.$refs.formValidator.reset();

		// Clear paramsPaid
		this.paramsPaid.PaymentVia = "";
		this.paramsPaid.PaymentBankID = "";
		this.paramsPaid.PaidDate = "";
		this.paidAccountID = "";
		this.paidCommission = {};
		this.tfimgFiles = [];

		// Fetch this invoice's billings so we can set commission_deducted per policy.
		this.clearInvoiceBillings();
		if (row && row.ID) {
			await this.listInvoiceBillings(row.ID);
			(this.invoiceBillings || []).forEach((b) => {
				this.$set(this.paidCommission, b.billing_id, false);
			});
		}
	},
	async handleUpdatePaid(){

		try{
			let result = await this.$refs.formValidator.validate();
			if (!result) {
				return;
			}
		} catch (e) {
			console.log(e)
			return
		}
		// transfer (PaymentVia=3) requires a payslip; branch-cash does not
		if(this.tfimgFiles.length == 0 && this.paidPaymentVia == 3){
			swal.fire({
			  title: this.$t("กรุณาเลือกไฟล์"),
			  text: '',
			  icon: "error",
			  confirmButtonClass: "btn btn-danger btn-fill",
			  buttonsStyling: false
			});
			return
		}

		// per-billing commission_deducted map: { "<billingId>": 0|1 }
		const commission = {};
		(this.invoiceBillings || []).forEach((b) => {
			commission[b.billing_id] = this.paidCommission[b.billing_id] ? 1 : 0;
		});

		let formData = new FormData();
        formData.append("ID", this.paramsPaid.ID);
        formData.append("PaymentVia", this.paidPaymentVia);
        formData.append("PaymentBankID", this.paidAccountID || 0);
        formData.append("commission", JSON.stringify(commission));
        // Multi-file: append each under the same field name so BE reads []FileHeader.
        this.tfimgFiles.forEach((f) => formData.append("Tfimg", f));
        formData.append("PaidDate", this.paramsPaid.PaidDate);

		this.paidData(formData)
          .then(resp => {
            this.listData(this.params);
			this.showPaidModal = false;
            swal.fire({
              title: this.$t("create_success"),
              text: ``,
              icon: "success",
              confirmButtonClass: "btn btn-success btn-fill",
              buttonsStyling: false
            });
          })
          .catch(err => {
            swal.fire({
              title: this.$t(err.response.data.Msg),
              text: '',
              icon: "error",
              confirmButtonClass: "btn btn-danger btn-fill",
              buttonsStyling: false
            });
          });

	},
	handleViewDetail(row){
		this.detail = row;
		this.showDetailModal = true;
		// Fetch enriched billing rows (policy_number + customer/owner) so the
		// modal can list them as clickable links into the transaction page.
		this.clearInvoiceBillings();
		if (row && row.ID) {
			this.listInvoiceBillings(row.ID);
		}
	},
	handlePolicyClick(row){
		// Navigate to the transaction list pre-filtered by policy number.
		// Same-tab navigation per product spec — closes the invoice context.
		if (!row || !row.policy_number) return;
		this.showDetailModal = false;
		this.$router.push({ path: "/lmg/v2/transaction", query: { policyNumber: row.policy_number } });
	},
	async handleCopyPolicyNumber(policyNumber){
		if (!policyNumber) return;
		try {
			// Modern clipboard API. Falls back to a hidden textarea+execCommand
			// for older browsers / non-HTTPS contexts where navigator.clipboard
			// is unavailable.
			if (navigator.clipboard && window.isSecureContext) {
				await navigator.clipboard.writeText(policyNumber);
			} else {
				const ta = document.createElement("textarea");
				ta.value = policyNumber;
				ta.style.position = "fixed";
				ta.style.opacity = "0";
				document.body.appendChild(ta);
				ta.select();
				document.execCommand("copy");
				document.body.removeChild(ta);
			}
			swal.fire({
				toast: true,
				position: "top-end",
				icon: "success",
				title: this.$t("copied") + ": " + policyNumber,
				showConfirmButton: false,
				timer: 1500,
			});
		} catch (e) {
			swal.fire({
				toast: true,
				position: "top-end",
				icon: "error",
				title: this.$t("copy_failed"),
				showConfirmButton: false,
				timer: 2000,
			});
		}
	},
	getPaidStatusClass(status){
		if (status == 1) return "status-pill status-pill--processing";  // รอชำระเงิน
		if (status == 2) return "status-pill status-pill--success";     // ชำระแล้ว
		if (status == 3) return "status-pill status-pill--danger";      // ยกเลิก
		return "status-pill status-pill--muted";
	},
	getPaymentViaName(via){
		const p = this.paidType.find(it => it.ID == via);
		return p ? p.Name : "-";
	},
	getBankName(bankID){
		if (!bankID) return "-";
		const list = this.tableBank || [];
		const b = list.find(it => it.ID == bankID);
		return b ? b.Name : `#${bankID}`;
	},
	handleViewInvoiceImage(){
		if (!this.detail.TranferImage) return;
		// Fetch through the API client so the JWT auth header is sent (the
		// /api/file/image endpoint is protected — opening its URL straight in a
		// new tab sent no token and returned "missing or malformed jwt").
		LmgService.FileImage(this.detail.TranferImage).then(res => {
			const url = window.URL.createObjectURL(res.data);
			window.open(url, "_blank");
		}).catch(() => {
			swal.fire({ title: "Error", text: "เปิดไฟล์ไม่สำเร็จ", icon: "error" });
		});
	},
	handlePDFBilling(row){
		this.reportData({InvoiceID : row.ID}).then(res => {
			const url = window.URL.createObjectURL(new Blob([res.data]));
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", `invoice-${row.ID}.pdf`); //or any other extension
			document.body.appendChild(link);
			link.click();
		}).catch(err => {
			swal.fire({
			  title: "Error",
			  text: '',
			  icon: "error",
			  confirmButtonClass: "btn btn-danger btn-fill",
			  buttonsStyling: false
			});
		});
	},
	onSelectionChange(selectedRows) {
        // Modal-only — track which bills the user has ticked for invoicing.
        this.selectedBillings = selectedRows;
      },
  }
};
</script>
<style>
/* segmented จ่ายเต็ม / หักคอมฯ toggle (invoice pay modal) */
.qp-basis { display: inline-flex; border: 1px solid #d8e0ea; border-radius: 7px; overflow: hidden; }
.qp-basis__btn {
  border: none; background: #fff; color: #7b8794;
  font: inherit; font-size: 12.5px; padding: 3px 10px; cursor: pointer; transition: background .12s, color .12s;
}
.qp-basis__btn + .qp-basis__btn { border-left: 1px solid #d8e0ea; }
.qp-basis__btn--on { background: #3b6ea8; color: #fff; }

.no-border-card .card-footer {
  border-top: 0;
}

/* multi-file transfer-proof picker (paid modal) */
.tf-files { list-style: none; margin: 8px 0 0; padding: 0; }
.tf-files li { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 4px 8px; border: 1px solid #e1e6ed; border-radius: 6px; margin-bottom: 4px; font-size: 13px; }
.tf-files__name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tf-files__rm { border: none; background: transparent; color: #c0392b; font-size: 18px; line-height: 1; cursor: pointer; padding: 0 4px; }

/* ─────────────────────────────────────────────────────────────
 * v2 invoice — page-level layout helpers (calm)
 *
 * The page wraps in <div class="calm"> so calm tokens cascade naturally.
 * These selectors are deliberately scoped to .calm so they can't bleed
 * onto v1 invoice page (which has no .calm ancestor).
 * ─────────────────────────────────────────────────────────────*/

/* Row-action icon buttons — match the v2 Transaction page theme
 * (calm ghost icon button: square, transparent, blue tint on hover).
 * Keyed under .calm so it can't bleed onto the v1 page. */
.calm .row-actions {
  display: inline-flex;
  gap: 4px;
  justify-content: flex-end;
}
.calm .rowbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  background: transparent;
  border: 1px solid transparent;
  color: var(--c-text-soft);
  border-radius: 6px;
  cursor: pointer;
  transition: all var(--c-fast) var(--c-ease);
  font-size: 12.5px;
}
.calm .rowbtn:hover {
  background: var(--c-blue-soft);
  border-color: var(--c-blue);
  color: var(--c-blue-deep);
}

.calm .invoice-filter-grid {
  /* 4 columns desktop, 2-up tablet, 1-up phone — keeps form fields aligned. */
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px 14px;
}
.calm .invoice-filter-grid .fld--span2 { grid-column: span 2; }
@media (max-width: 1100px) { .calm .invoice-filter-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 600px)  { .calm .invoice-filter-grid { grid-template-columns: 1fr; } .calm .invoice-filter-grid .fld--span2 { grid-column: span 1; } }

/* Search/clear button row sits flush right under the filter grid,
   separated by a thin rule. */
.calm .invoice-filter-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid var(--c-border);
}

/* Table-card header strip — title + meta on the left, toolbar on the right.
   Without flex layout these elements stacked vertically and overlapped the
   table content. */
.calm .invoice-table-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 18px 22px;
  border-bottom: 1px solid var(--c-border);
}
.calm .invoice-table-toolbar {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

/* ─────────────────────────────────────────────────────────────
 * v2 invoice — create-invoice modal (calm theme)
 *
 * The Argon <modal> portals its content out of the page-level .calm root,
 * so the calm CSS variables don't cascade naturally. The modal body and
 * footer therefore each get a `.calm` wrapper to bring the tokens with them.
 * Selectors below are unscoped (we're in a global <style> block) but keyed to
 * .calm-modal / .calm-modal-body / .bm-* prefixes so they can't bleed onto
 * v1 modals.
 * ─────────────────────────────────────────────────────────────*/
.calm-modal .modal-content {
  background: var(--c-bg, #f5f7fa);
  border: 1px solid var(--c-border, #e4e9f1);
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(44, 62, 80, 0.16);
}
.calm-modal .modal-header,
.calm-modal .modal-body,
.calm-modal .modal-footer {
  background: transparent;
  border: 0;
}
.calm-modal .modal-header { padding: 0; }
.calm-modal .modal-body { padding: 0; }
.calm-modal .modal-footer { padding: 14px 22px; border-top: 1px solid var(--c-border); background: var(--c-surface); }
.calm-modal .close { color: var(--c-text-soft); opacity: 0.7; }
.calm-modal .close:hover { opacity: 1; color: var(--c-text); }

.calm-modal-body { padding: 22px 26px 4px; }

.bm-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}
.bm-title {
  font-size: 22px;
  font-weight: 600;
  color: var(--c-text);
  margin: 4px 0 6px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.bm-title i { color: var(--c-blue); font-size: 18px; }
.bm-meta {
  text-align: right;
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: 10px;
  padding: 10px 16px;
  min-width: 140px;
}
.bm-meta__value {
  font-size: 22px;
  font-weight: 600;
  color: var(--c-blue-deep);
  font-variant-numeric: tabular-nums;
  display: flex;
  align-items: baseline;
  gap: 4px;
}

/* ยอดรวมชำระ ก่อน → หลัง (add-to-invoice mode) */
.bm-meta__delta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 4px;
  font-size: 12.5px;
  font-variant-numeric: tabular-nums;
}
.bm-meta__delta i { font-size: 10px; opacity: 0.6; }
.bm-meta__delta strong { color: var(--c-blue-deep); }

.bm-section {
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: 10px;
  padding: 18px 20px;
  margin-bottom: 14px;
}
.bm-section--table { padding: 0; overflow: hidden; }
.bm-section__head {
  margin-bottom: 14px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.bm-section--table .bm-section__head {
  padding: 16px 20px 14px;
  border-bottom: 1px solid var(--c-border);
  margin-bottom: 0;
}

.bm-filter-grid {
  /* 4-up on desktop, 2-up tablet, 1-up phone */
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px 14px;
}
@media (max-width: 1100px) { .bm-filter-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 600px)  { .bm-filter-grid { grid-template-columns: 1fr; } }

.bm-search-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--c-border);
}

.bm-table-wrap { background: var(--c-surface); }

.bm-note {
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: 10px;
  padding: 16px 20px;
  margin-top: 14px;
}

.calm-modal-foot {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  width: 100%;
}

/* ---- Invoice detail modal styling ---- */
.invoice-detail .detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #f5f7fb 0%, #ecf0fb 100%);
  border-left: 4px solid #5e72e4;
}
.invoice-detail .detail-header h4 {
  margin: 0;
  font-weight: 600;
  color: #32325d;
}
.invoice-detail .detail-header .detail-id {
  font-weight: 400;
  color: #8898aa;
  margin-left: 0.5rem;
  font-family: monospace;
}

.invoice-detail .detail-section {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem 0.25rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 2px rgba(50, 50, 93, 0.04);
}
.invoice-detail .detail-section-header {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8898aa;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px dashed #e9ecef;
}
.invoice-detail .detail-section-header i {
  margin-right: 0.5rem;
  color: #5e72e4;
  font-size: 0.9rem;
}
.invoice-detail .detail-section--financial {
  background: #fafcff;
  border-color: #dfe7f5;
}
.invoice-detail .detail-section--financial .detail-section-header i {
  color: #2dce89;
}

.invoice-detail .status-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.invoice-detail .status-pill i { margin-right: 0.35rem; }
.invoice-detail .status-pill--success    { background: #e6f8ef; color: #1aae6f; }
.invoice-detail .status-pill--processing { background: #fff4e0; color: #c58b00; }
.invoice-detail .status-pill--danger     { background: #fde4e4; color: #d6436a; }
.invoice-detail .status-pill--muted      { background: #eef1f7; color: #525f7f; }

.invoice-detail .form-control[readonly] {
  background-color: #fbfcfe;
  cursor: default;
}

/* billing list chip grid */
.invoice-detail .billing-chip {
  display: inline-block;
  padding: 0.25rem 0.7rem;
  margin: 0.15rem 0.2rem 0.15rem 0;
  background: #eef3ff;
  color: #324cdd;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.8rem;
}
</style>
